import { formatMoney } from '@shopify/theme-currency';

/**
 * @class CartDiscount
 * @extends HTMLElement
 * @description Cart discount component, this component is used to apply and clear discounts in the cart and cart drawer.
 * @documentation https://shopify.dev/docs/api/ajax/reference/cart#update-discounts-in-the-cart
 */
class CartDiscount extends HTMLElement {
  cartItems = document.querySelector('cart-items');
  applyButton = this.querySelector('#apply-discount-btn');
  discountCodeError = this.querySelector('#discount-code-error');
  discountCodeErrorText = this.discountCodeError.dataset.errorText;
  discountCodeInput = this.querySelector('[data-discount-code-input]');
  totalCartSelector = this.querySelector('[data-cart-total]');
  progressBar = this.querySelector('#cart-progress-bar');
  cartThreshold = parseInt(this.progressBar?.dataset.cartThreshold) || undefined;
  remainingAmountEl = this.querySelector('#remaining_amount');
  clearButton = this.querySelector('[data-clear-discount-btn]');

  connectedCallback() {
    this.discountCodeInput?.addEventListener('input', () => {
      const value = this.discountCodeInput.value.trim();
      value.length > 0
        ? this.applyButton.classList.remove('hidden')
        : this.applyButton.classList.add('hidden');
    });

    this.discountCodeInput?.addEventListener('keyup', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'Enter') {
        const value = this.discountCodeInput.value.trim();
        if (!value) return;
        this.applyDiscount(value);
      }
    });

    this.applyButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const value = this.discountCodeInput.value.trim();
      if (!value) return;
      this.applyDiscount(value);
    });

    this.clearButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.clearDiscount();
    });
  }

  /**
   * @function applyDiscount
   * @description Apply the discount code to the cart
   * @param {string} code - The discount code to apply
   * @documentation https://shopify.dev/docs/api/ajax/reference/cart#update-discounts-in-the-cart
   */
  async applyDiscount(code) {
    if (this.cartItems) this.cartItems.enableLoading();

    this.discountCodeInput.setAttribute('disabled', 'true');
    this.applyButton.setAttribute('disabled', 'true');
    this.discountCodeInput.disabled = 'true';
    this.applyButton.innerHTML = this.applyButton.dataset.applying_str;
    this.applyButton.classList.add('text-dark-gray');

    try {
      // Check if there are already discount codes, to make sure we don't delete them when applying a new one
      const discountFetch = await fetch(`/cart.json`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const cartData = await discountFetch.json();

      // Collect all discount codes from the current cart
      let discountCodes = cartData?.discount_codes?.map((discount) => discount.code) || [];
      // Add the new discount code to the list
      discountCodes.push(code);
      // Join the discount codes with a comma
      const discountCodesString = discountCodes.join(',');

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: discountCodesString,
        }),
      });
      const data = await response.json();

      // Check if the new discount code is applied successfully
      const isDiscountApplied = data?.discount_codes?.some(
        (discount) => discount.code === code && discount.applicable === true
      );

      // Clear local storage and show error message if discount is not applied
      if (!response.ok || isDiscountApplied === false) {
        this.clearLocalStorage();
        const errorMessage = data?.errors?.discount_code[0]?.message || this.discountCodeErrorText;
        this.discountCodeError.innerHTML = errorMessage;
        throw new Error(errorMessage);
      }

      // When discount is applied continue with the rest of the code
      this.discountCodeError.innerHTML = '';

      const localStorageValue = {
        code: code.trim(),
        totalCart: data.items_subtotal_price,
      };
      localStorage.setItem('discountCode', JSON.stringify(localStorageValue));

      if (this.progressBar && data.items_subtotal_price * 100 < this.cartThreshold * 100) {
        const progress_bar_width = this.calcPercentage(data.total_price, this.cartThreshold);
        const remainingAmount = (this.cartThreshold - data.total_price).toFixed(2);
        this.progressBar.style.width = `${progress_bar_width}%`;
        this.remainingAmountEl.innerHTML = remainingAmount.replace('.', ',');
      }
      // Refresh the items on the cart page
      if (this.cartItems) await this.cartItems.refreshState();
    } catch (error) {
      console.error('Error:', error);
      this.discountCodeInput.removeAttribute('disabled');
      this.applyButton.classList.remove('text-dark-gray');
      this.applyButton.innerHTML = this.applyButton.dataset.apply_str;
      this.applyButton.removeAttribute('disabled');
    } finally {
      if (this.cartItems) this.cartItems.disableLoading();
    }
  }

  /**
   * @function clearDiscount
   * @description Clear the discount code from the cart
   * @documentation https://shopify.dev/docs/api/ajax/reference/cart#update-discounts-in-the-cart
   */
  async clearDiscount() {
    this.discountCodeInput.setAttribute('disabled', 'true');
    this.applyButton.setAttribute('disabled', 'true');

    this.clearButton.innerHTML = this.clearButton.dataset.removingStr;
    this.clearButton.classList.add('disabled');

    try {
      // Toggle cart loading state
      if (this.cartItems) this.cartItems.enableLoading();

      const clearDiscountResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: '',
        }),
      });
      const cartData = await clearDiscountResponse.json();

      this.clearLocalStorage();
      this.discountCodeInput.value = '';
      this.discountCodeError.innerHTML = '';

      if (this.progressBar && cartData.items_subtotal_price * 100 < this.cartThreshold * 100) {
        const progress_bar_width = this.calcPercentage(
          cartData.items_subtotal_price,
          this.cartThreshold
        );
        const remainingAmount = (this.cartThreshold - cartData.items_subtotal_price).toFixed(2);
        this.progressBar.style.width = `${progress_bar_width}%`;
        this.remainingAmountEl.innerHTML = remainingAmount.replace('.', ',');
      }

      if (this.totalCartSelector) {
        this.totalCartSelector.innerHTML = formatMoney(
          cartData.items_subtotal_price,
          shopMoneyFormat
        );
      }

      if (this.cartItems) await this.cartItems.refreshState();
    } catch (error) {
      console.error('Error clearing discount:', error);
      this.applyButton.removeAttribute('disabled');
      this.clearButton.innerHTML = this.clearButton.dataset.removeStr;
      this.clearButton.classList.remove('disabled');
      this.discountCodeInput.removeAttribute('disabled');
    } finally {
      this.cartItems.disableLoading();
      if (this.cartItems) await this.cartItems.refreshState();
    }
  }

  clearLocalStorage() {
    const localStorageValue = localStorage.discountCode;
    if (this.totalCartSelector && localStorageValue) {
      this.totalCartSelector.innerHTML = JSON.parse(localStorage.discountCode).totalCart;
      localStorage.removeItem('discountCode');
    }
  }

  calcPercentage(x, y, fixed = 2) {
    const percent = (x / y) * 100;
    return percent.toFixed(fixed);
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
